import React from 'react';
import InertiaTable from 'laravel-inertia-react-table';
import {Link, usePage} from '@inertiajs/react';
import {Alert, Card, Container} from 'react-bootstrap';
import {usePolling} from 'laravel-inertia-backend';
import TimestampTableCell from "../../Components/TimestampTableCell.jsx";
import StatusBadgeTableCell from "../../Components/StatusBadgeTableCell.jsx";
import {makeChangesCountLinkTableCell} from "../../Components/ChangesCountLinkTableCell.jsx";
import HelpTooltip from "../../Components/HelpTooltip.jsx";

const ChangesCountLinkTableCell = makeChangesCountLinkTableCell('/stock/b2b-availability/runs');

export default function B2bAvailability() {
    const {b2b_stock_sync_runs, col_definitions, stock_sync_settings, stock_sync_stats} = usePage().props;
    usePolling('b2b_stock_sync_runs');

    const enabled = stock_sync_settings?.enabled ?? false;
    const threshold = stock_sync_settings?.threshold ?? '–';
    const mailRecipients = stock_sync_settings?.mail_recipients || 'nicht hinterlegt';

    const managedTotal = stock_sync_stats?.managed_total ?? null;
    const withStock = stock_sync_stats?.with_stock ?? null;
    const withoutStock = stock_sync_stats?.without_stock ?? null;

    return (
        <>
            <Container fluid className="pt-2">
                <div className="mt-1 mb-3 d-flex">
                    <h1>B2B-Lagerverfügbarkeit</h1>
                </div>

                <Card className="mb-4">
                    <Card.Body>
                        <Card.Title>Wie funktioniert der Abgleich?</Card.Title>
                        <Card.Text className="mb-3">
                            Ändert sich der Lagerbestand eines Artikels in Weclapp, schaltet dieser Prozess
                            den Artikel im B2B-Shop automatisch auf „Auf Lager" bzw. „In Zulauf".
                        </Card.Text>
                        <Card.Text as="div">
                            <ul className="mb-2 ps-3">
                                <li>
                                    <strong>Datenquelle:</strong> Weclapp (Mandant Feuerstein), Lager SNV Weilheim.
                                </li>
                                <li>
                                    <strong>Regel:</strong> Bestand kleiner als <strong>{threshold}</strong> Stück
                                    {' '}⇒ Artikel wird im Shop als „In Zulauf" geführt.
                                </li>
                                <li>
                                    <strong>Turnus:</strong> stündlich, automatisch über den Scheduler.
                                </li>
                                <li>
                                    <strong>Abschluss:</strong> Umschaltung im Shop je Artikel, zusätzlich einmal
                                    täglich eine Zusammenfassungs-Mail an {mailRecipients}.
                                </li>
                            </ul>
                        </Card.Text>
                        {managedTotal !== null && (
                            <Card.Text className="text-muted mb-2">
                                <strong>{managedTotal}</strong> Artikel automatisch überwacht · davon{' '}
                                <strong>{withStock}</strong> aktuell mit Bestand im Lager SNV (werden je Lauf
                                geprüft) · <strong>{withoutStock}</strong> ohne Bestandssatz (werden übersprungen).
                                <br />
                                Hinweis: Die Spalte „Geprüft" im Log unten zählt nur die Artikel mit
                                Bestandssatz, nicht alle {managedTotal} überwachten Artikel.
                            </Card.Text>
                        )}
                        <Alert variant={enabled ? 'success' : 'warning'} className="d-flex justify-content-between align-items-center mb-3">
                            <span>
                                Automatischer Abgleich ist aktuell <strong>{enabled ? 'aktiviert' : 'deaktiviert'}</strong>.
                            </span>
                            <Link href="/settings" className="btn btn-outline-secondary btn-sm ms-3">
                                Einstellungen ändern
                            </Link>
                        </Alert>
                        <Link href="/stock/overview?ueberwacht=1" className="btn btn-primary btn-sm">
                            Überwachte Artikel ansehen
                        </Link>
                    </Card.Body>
                </Card>
            </Container>

            <InertiaTable
                key={'b2b-stock-sync-runs-table'}
                data={b2b_stock_sync_runs}
                title={
                    <span className="d-inline-flex align-items-center gap-2">
                        Live-Log
                        <HelpTooltip placement="right" size={0.95}>
                            <div className="text-start">
                                Protokoll der bisherigen Läufe. Aktualisiert sich automatisch, sobald ein
                                neuer Lauf abgeschlossen ist.
                            </div>
                        </HelpTooltip>
                    </span>
                }
                colDefinitions={[...col_definitions]}
                customCellRendererMap={{
                    'started_at': TimestampTableCell,
                    'status': StatusBadgeTableCell,
                    'changes_count': ChangesCountLinkTableCell,
                }}
            />
        </>
    );
}
